import { useEffect, useState, useCallback } from "react";
import { Loader2, RefreshCw, Mountain, Grid2X2 } from "lucide-react";
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
    pointerWithin,
} from "@dnd-kit/core";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getBedOccupancy, placeCrop, batchPositionMoves, deleteCrop } from "@/lib/api";
import { BedGrid } from "./BedGrid";
import { RootView } from "./RootView";
import { CropPalette } from "./CropPalette";
import { CropChip } from "./CropChip";
import { CollisionDialog } from "./CollisionDialog";
import { CropEditDialog } from "./CropEditDialog";
import { FAMILY_TOKENS } from "./color-tokens";

/**
 * Orchestrator for the bed designer view.
 *
 * Drag flows handled here:
 *   - palette → empty cell      → place
 *   - palette → occupied cell   → CollisionDialog (displace)
 *   - cell    → empty cell      → move
 *   - cell    → occupied cell   → CollisionDialog (swap or displace)
 *   - cell    → palette         → un-place
 *   - any     → off-target      → no-op
 *
 * State:
 *   - `occupancy` is the server truth.
 *   - `pendingPositions` lets us render moves optimistically before the API
 *     round-trip completes; on success we reload, on failure we revert.
 */
export function BedGridEditor({ bedId, onChange }) {
    const [occupancy, setOccupancy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [activeCrop, setActiveCrop] = useState(null);
    const [collision, setCollision] = useState(null);
    const [editingCrop, setEditingCrop] = useState(null);
    // Underground vs top-down view toggle. Defaults to top-down (the
    // editable mode); underground is purely visual / educational.
    const [viewMode, setViewMode] = useState("top");

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    );

    const load = useCallback(async () => {
        try {
            setOccupancy(await getBedOccupancy(bedId));
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message || "Couldn't load grid");
        }
    }, [bedId]);

    useEffect(() => {
        setLoading(true);
        load().finally(() => setLoading(false));
    }, [load]);

    const refresh = async () => {
        setRefreshing(true);
        await load();
        setRefreshing(false);
    };

    // ---------------- Drag handlers ----------------

    const handleDragStart = (event) => {
        const cropId = event.active.data.current?.cropId;
        const fromPlaced = occupancy.crops?.[cropId];
        const fromPalette = (occupancy.unplaced || []).find(u => u.id === cropId);
        setActiveCrop(fromPlaced || fromPalette || null);
    };

    const handleDragCancel = () => setActiveCrop(null);

    const handleDragEnd = async (event) => {
        const dragged = activeCrop;
        setActiveCrop(null);
        if (!dragged) return;

        const { active, over } = event;
        if (!over) return;

        const overData = over.data.current;
        if (!overData) return;

        // -------- drop on palette: un-place --------
        if (overData.kind === "palette") {
            if (!active.data.current?.fromPosition) return; // already in palette
            try {
                await placeCrop(dragged.id, null);
                toast.success(`Sent ${dragged.crop_name} back to the palette`);
                await load();
                onChange?.();
            } catch (e) {
                toast.error(extractErr(e));
            }
            return;
        }

        // -------- drop on a grid cell --------
        if (overData.kind !== "cell") return;
        const { row, col, cropId: cellCropId } = overData;

        // Same source cell — noop
        if (cellCropId === dragged.id) return;

        const targetCrop = cellCropId ? occupancy.crops[cellCropId] : null;
        if (targetCrop) {
            // Conflict — show dialog
            setCollision({
                sourceCrop: dragged,
                targetCrop,
                targetCell: { row, col },
            });
            return;
        }

        // Empty cell — straight place / move (with auto-split if over-capacity)
        try {
            const result = await placeCrop(dragged.id, {
                row, col, row_span: 1, col_span: 1,
            });
            if (result.remainder) {
                toast.success(
                    `Placed ${result.placed.plant_count} × ${dragged.crop_name} at (${row},${col}). ${result.remainder.plant_count} returned to the palette.`,
                );
            } else {
                toast.success(`Placed ${result.placed.plant_count} × ${dragged.crop_name} at row ${row}, col ${col}`);
            }
            await load();
            onChange?.();
        } catch (e) {
            toast.error(extractErr(e));
        }
    };

    // ---------------- Collision resolutions ----------------

    const resolveSwap = async () => {
        if (!collision) return;
        const { sourceCrop, targetCrop, targetCell } = collision;
        const sourcePos = sourceCrop.position;
        if (!sourcePos) return;
        try {
            await batchPositionMoves([
                {
                    crop_id: sourceCrop.id,
                    position: { row: targetCell.row, col: targetCell.col, row_span: 1, col_span: 1 },
                },
                {
                    crop_id: targetCrop.id,
                    position: { row: sourcePos.row, col: sourcePos.col, row_span: 1, col_span: 1 },
                },
            ]);
            toast.success(`Swapped ${sourceCrop.crop_name} ↔ ${targetCrop.crop_name}`);
            setCollision(null);
            await load();
            onChange?.();
        } catch (e) {
            toast.error(extractErr(e));
        }
    };

    const resolveDisplace = async () => {
        if (!collision) return;
        const { sourceCrop, targetCrop, targetCell } = collision;
        try {
            // Two-step in a single batch: free the target, place the source.
            await batchPositionMoves([
                { crop_id: targetCrop.id, position: null },
                {
                    crop_id: sourceCrop.id,
                    position: { row: targetCell.row, col: targetCell.col, row_span: 1, col_span: 1 },
                },
            ]);
            toast.success(`${targetCrop.crop_name} sent to palette · ${sourceCrop.crop_name} placed`);
            setCollision(null);
            await load();
            onChange?.();
        } catch (e) {
            toast.error(extractErr(e));
        }
    };

    // ---------------- Edit / Delete callbacks ----------------

    const handleEdit = (crop) => setEditingCrop(crop);

    const handleDelete = async (crop) => {
        if (!window.confirm(`Delete ${crop.crop_name}? This cannot be undone.`)) return;
        try {
            await deleteCrop(crop.id);
            toast.success(`${crop.crop_name} deleted`);
            await load();
            onChange?.();
        } catch (e) {
            toast.error(extractErr(e));
        }
    };

    // ---------------- Render ----------------

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-[var(--vm-text-muted)] text-sm" data-testid="grid-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading grid…
            </div>
        );
    }
    if (!occupancy) {
        return <div className="text-[var(--vm-text-muted)]">No grid data available.</div>;
    }

    const placedCount = Object.keys(occupancy.crops || {}).length;
    const unplacedCount = (occupancy.unplaced || []).length;

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            <div className="space-y-4" data-testid={`bed-grid-editor-${bedId}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm text-[var(--vm-text-secondary)]">
                        <span className="font-mono-grid">
                            {occupancy.grid_rows} × {occupancy.grid_cols}
                        </span>{" "}
                        grid · {placedCount} placed · {unplacedCount} unplaced
                    </div>
                    <div className="flex items-center gap-2">
                        {/* View toggle: top-down (editable) ↔ underground (read-only) */}
                        <div
                            className="inline-flex rounded-full border border-[var(--vm-border)] p-0.5 bg-[var(--vm-card)]/60"
                            role="group"
                            aria-label="Bed view mode"
                            data-testid={`grid-view-toggle-${bedId}`}
                        >
                            <button
                                type="button"
                                onClick={() => setViewMode("top")}
                                aria-pressed={viewMode === "top"}
                                data-testid={`grid-view-top-${bedId}`}
                                title="Top-down — editable grid"
                                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                    viewMode === "top"
                                        ? "bg-[var(--vm-accent)] text-[var(--vm-bg)]"
                                        : "text-[var(--vm-text-secondary)] hover:text-[var(--vm-text)]"
                                }`}
                            >
                                <Grid2X2 className="w-3.5 h-3.5" /> Top
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode("underground")}
                                aria-pressed={viewMode === "underground"}
                                data-testid={`grid-view-underground-${bedId}`}
                                title="Underground — see what's below the soil"
                                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                    viewMode === "underground"
                                        ? "bg-[var(--vm-accent)] text-[var(--vm-bg)]"
                                        : "text-[var(--vm-text-secondary)] hover:text-[var(--vm-text)]"
                                }`}
                            >
                                <Mountain className="w-3.5 h-3.5" /> Underground
                            </button>
                        </div>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={refresh}
                            disabled={refreshing}
                            data-testid={`grid-refresh-${bedId}`}
                            className="text-[var(--vm-text-muted)] hover:text-[var(--vm-text)]"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
                            Refresh
                        </Button>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 min-w-0">
                        {viewMode === "underground"
                            ? <RootView occupancy={occupancy} />
                            : <BedGrid
                                  occupancy={occupancy}
                                  activeDrag={activeCrop}
                                  onEditCrop={handleEdit}
                                  onDeleteCrop={handleDelete}
                              />}
                    </div>
                    {viewMode === "top" && <CropPalette unplaced={occupancy.unplaced} />}
                </div>

                <FamilyLegend />

                <p className="text-xs text-[var(--vm-text-muted)] italic">
                    Drag a crop chip onto a cell to place it. Click any chip for details, edit or delete. Drop back on the palette to free a cell.
                </p>
            </div>

            <DragOverlay dropAnimation={{ duration: 180 }}>
                {activeCrop ? (
                    <div className="rotate-1 shadow-lg">
                        <CropChip crop={activeCrop} variant="overlay" />
                    </div>
                ) : null}
            </DragOverlay>

            <CollisionDialog
                collision={collision}
                onSwap={resolveSwap}
                onDisplace={resolveDisplace}
                onCancel={() => setCollision(null)}
            />

            {editingCrop && (
                <CropEditDialog
                    crop={editingCrop}
                    open={!!editingCrop}
                    onOpenChange={(o) => { if (!o) setEditingCrop(null); }}
                    onSaved={async () => { await load(); onChange?.(); }}
                />
            )}
        </DndContext>
    );
}

function extractErr(e) {
    const detail = e.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (detail?.message) return detail.message;
    if (detail?.code === "conflict") return "That cell is occupied — try another.";
    return e.message || "Something went wrong";
}

function FamilyLegend() {
    return (
        <div className="flex flex-wrap gap-2 text-[10px] text-[var(--vm-text-muted)]" data-testid="family-legend">
            <span className="overline mr-1 text-[var(--vm-sage)]">Rotation family</span>
            {Object.entries(FAMILY_TOKENS)
                .filter(([key]) => key !== "other")
                .map(([family, token]) => (
                    <span
                        key={family}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${token.bg} ${token.border} ${token.text}`}
                    >
                        {token.label}
                    </span>
                ))}
        </div>
    );
}
